import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Labels from "../labels/Labels";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// JSON Syntax Highlighter Component
const JsonSyntaxHighlighter = ({ jsonString, compact = false }) => {
  const syntaxHighlight = (json) => {
    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      function (match) {
        let cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
          } else {
            cls = "string";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
        } else if (/null/.test(match)) {
          cls = "null";
        }
        return '<span class="' + cls + '">' + match + "</span>";
      }
    );
  };

  const highlightedJson = syntaxHighlight(jsonString);

  return (
    <Box
      component="pre"
      sx={{
        fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
        fontSize: compact ? "0.7rem" : "0.75rem",
        lineHeight: compact ? 1.2 : 1.4,
        margin: 0,
        padding: compact ? "4px" : "8px",
        backgroundColor: compact ? "transparent" : "rgba(0, 0, 0, 0.02)",
        borderRadius: compact ? 0 : "4px",
        border: compact ? "none" : "1px solid rgba(0, 0, 0, 0.1)",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        overflow: "auto",
        textAlign: "left",
        "& .string": { color: "#22863a" },
        "& .number": { color: "#005cc5" },
        "& .boolean": { color: "#d73a49" },
        "& .null": { color: "#6f42c1" },
        "& .key": { color: "#032f62", fontWeight: "bold" },
      }}
      dangerouslySetInnerHTML={{ __html: highlightedJson }}
    />
  );
};

// Enhanced JSON Modal Component
const JsonModal = ({ data, children }) => {
  const [open, setOpen] = useState(false);

  if (!data) return children;

  const getFormattedJson = (rawData) => {
    try {
      if (typeof rawData === "string") {
        try {
          return JSON.stringify(JSON.parse(rawData), null, 2);
        } catch {
          return rawData;
        }
      }
      return JSON.stringify(rawData, null, 2);
    } catch {
      return String(rawData);
    }
  };

  const handleCopy = () => {
    const textToCopy = getFormattedJson(data);
    navigator.clipboard.writeText(textToCopy);
  };

  return (
    <>
      <Box onClick={() => setOpen(true)}>{children}</Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">JSON Details</Typography>
          <Box>
            <IconButton onClick={handleCopy} sx={{ color: "white", mr: 1 }}>
              <ContentCopyIcon />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <JsonSyntaxHighlighter jsonString={getFormattedJson(data)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function PayMeNowList(props) {
  const { isLoading, isError, data } = props;

  const columns = [
    {
      field: "idBulkDetail",
      headerName: Labels.getLabel("payMeNow.column.id", "ID"),
      width: 80,
    },

    {
      field: "status",
      headerName: Labels.getLabel("payMeNow.column.status", "Status"),
      width: 180,
      renderCell: (params) => {
        const status = params.value || "";
        const error = params.row.error || "";
        const isProcessed =
          status.toUpperCase() === "PROCESSED" ||
          status.toUpperCase() === "COMPLETED" ||
          status.toUpperCase() === "SUCCESS";
        const color = isProcessed ? "success" : "error";

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: `${color}.main`,
                color: "white",
                fontSize: "0.875rem",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {status}
            </Box>
            {error && (
              <Typography
                variant="caption"
                sx={{ fontSize: "0.7rem", color: "text.secondary" }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      },
    },

    {
      field: "extraInfo02",
      headerName: Labels.getLabel("payMeNow.column.detail", "Details"),
      width: 350,
      renderCell: (params) => {
        const jsonData = params.value;
        if (!jsonData || jsonData === "{}") return <span>-</span>;

        try {
          const parsedData =
            typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
          const virtualCompanyId = parsedData.virtualCompanyId || "-";
          const payStubNumber = parsedData.payStubNumber || "-";

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                  <strong>PayStub #:</strong> {payStubNumber}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                  <strong>Virtual Co ID:</strong> {virtualCompanyId}
                </Typography>
              </Box>
              <JsonModal data={jsonData}>
                <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                  <InfoIcon sx={{ fontSize: 18, color: "primary.main" }} />
                </IconButton>
              </JsonModal>
            </Box>
          );
        } catch (error) {
          return (
            <JsonModal data={jsonData}>
              <Box
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" color="error">
                  Invalid JSON
                </Typography>
                <InfoIcon
                  sx={{ ml: 0.5, fontSize: 16, color: "text.secondary" }}
                />
              </Box>
            </JsonModal>
          );
        }
      },
    },
    {
      field: "netAmount",
      headerName: Labels.getLabel("payMeNow.column.netAmount", "Net Amount"),
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        const jsonData = params.row.extraInfo02;
        if (!jsonData || jsonData === "{}") return <span>-</span>;

        try {
          const parsedData =
            typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
          return parsedData.netAmount || "-";
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "totalMoneyPaid",
      headerName: Labels.getLabel("payMeNow.column.totalPaid", "Total Paid"),
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        const jsonData = params.row.extraInfo02;
        if (!jsonData || jsonData === "{}") return <span>-</span>;

        try {
          const parsedData =
            typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
          return parsedData.totalMoneyPaidFromAdvances || "-";
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "creationDate",
      headerName: Labels.getLabel(
        "payMeNow.column.createdDate",
        "Created Date"
      ),
      width: 180,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel(
        "payMeNow.column.updatedDate",
        "Updated Date"
      ),
      width: 180,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleString();
      },
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {Labels.getLabel("payMeNow.loading", "Loading PayMeNow data...")}
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {Labels.getLabel(
          "payMeNow.error",
          "Error loading PayMeNow data. Please try again."
        )}
      </Alert>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {Labels.getLabel("payMeNow.noData", "No PayMeNow records found.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <DataGrid
        rows={data.data}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        disableSelectionOnClick
        getRowId={(row) => row.idBulkDetail}
        initialState={{
          pagination: {
            pageSize: 10,
            page: 0,
          },
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5",
            fontSize: "0.875rem",
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
}
